import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n";

const BADGE_COLORS = [
  { bg: "#FDE7E7", fg: "#D7352B" },
  { bg: "#FFF2C4", fg: "#B98A00" },
  { bg: "#E3EFFC", fg: "#2E7CD6" },
  { bg: "#E6F4EA", fg: "#3D9B4F" },
];

export default function Programs() {
  const { t, c } = useLang();
  return (
    <>
      <section className="pt-44 pb-16 bg-cream grain relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sage blur-3xl" />
        <div className="absolute top-32 left-1/4 w-40 h-40 rounded-full bg-amber/15 blur-2xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-tangerine">{t("prog_overline")}</p>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-forest tracking-tight max-w-3xl leading-tight">
              {t("prog_h1")}
            </h1>
            <p className="mt-6 text-bark text-base sm:text-lg leading-relaxed max-w-2xl">{t("prog_sub")}</p>
          </Reveal>
        </div>
      </section>

      <Marquee items={c.programsMarquee} />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 space-y-24">
          {c.programs.map((p, i) => {
            const col = BADGE_COLORS[i % BADGE_COLORS.length];
            return (
              <Reveal key={p.id}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div className="relative">
                    <div className="hero-blob overflow-hidden aspect-[4/3] shadow-2xl shadow-forest/15">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                    <span className="absolute -top-4 left-6 rounded-full px-5 py-2 text-xs font-bold shadow-lg" style={{ background: col.bg, color: col.fg }}>{p.age}</span>
                  </div>
                  <div>
                    <p className="font-accent text-5xl font-semibold" style={{ color: col.fg, opacity: 0.85 }}>0{i + 1}</p>
                    <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-forest tracking-tight">{p.title}</h2>
                    <p className="mt-5 text-bark leading-relaxed">{p.body}</p>
                    <ul className="mt-6 flex flex-wrap gap-2.5">
                      {p.tags.map((tag) => (
                        <li key={tag} className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold" style={{ background: col.bg, color: col.fg }}>
                          <Check className="w-3.5 h-3.5" /> {tag}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" data-testid={`${p.testId}-cta`}
                          className="group mt-8 inline-flex items-center gap-2 rounded-full bg-tangerine text-white px-7 py-3.5 font-display font-bold hover:bg-clay transition-colors shadow-lg shadow-tangerine/30">
                      {t("prog_cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <div className="rounded-[2rem] bg-white border border-bone p-8 sm:p-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <img src={IMAGES.funshine} alt="FunShine Digital curriculum" className="w-full max-w-[220px] mx-auto" />
              <div className="md:col-span-2">
                <h3 className="font-display font-extrabold text-2xl text-forest">{t("curr_h3")}</h3>
                <p className="mt-4 text-bark text-sm sm:text-base leading-relaxed">{t("curr_p")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
